import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Send, CheckCircle, AlertCircle, ChevronDown, BarChart3, TrendingUp, Smartphone, Bell, Zap, Search, X, Settings } from 'lucide-react';
import { apiFetch } from '../api';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';

export default function FCMForm({ setIsLoading, setSuccess, setError, fetchStats, API_BASE_URL, loginId, deliveryMode }) {

  const { client } = useWebSocket();
  
  const [deviceToken, setDeviceToken] = useState('');
  const [topic, setTopic] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [appNames, setAppNames] = useState([]);
  const [selectedAppName, setSelectedAppName] = useState('');
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [successLocal, setSuccessLocal] = useState('');
  const [errorLocal, setErrorLocal] = useState('');
  const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // New states for improved dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationType, setNotificationType] = useState('device'); // 'device' or 'topic'

  // New state for FCM version selection
  const [fcmVersion, setFcmVersion] = useState('new'); // 'new' or 'old'

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter app names based on search term
  const filteredAppNames = appNames.filter(name =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Clear messages
  useEffect(() => {
    if (successLocal || errorLocal) {
      const timer = setTimeout(() => {
        setSuccessLocal('');
        setErrorLocal('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successLocal, errorLocal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Fetch app names function
  const fetchAppNames = async () => {
    setIsAppNamesLoading(true);
    setErrorLocal(''); // Clear previous local errors

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setErrorLocal('Socket connection failed.');
          setIsAppNamesLoading(false);
          return;
        }

        const topic = `/topic/getAppName/${loginId}`;
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

            if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
              const appNames = data.Response.map(item => item.appName).filter(Boolean);
              setAppNames(appNames);

              if (appNames.length > 0 && !selectedAppName) {
                setSelectedAppName(appNames[0]);
              }
            } else {
              setErrorLocal('Failed to load app names via socket.');
            }
          } catch (e) {
            console.error('Failed to parse app names socket', e);
            setErrorLocal('Failed to parse app names.');
          } finally {
            subscription.unsubscribe();
            setIsAppNamesLoading(false);
          }
        });

        // Publish request
        client.publish({
          destination: '/app/getAppName',
          body: JSON.stringify({})
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/getAppName`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
          const appNames = data.Response.map(item => item.appName).filter(Boolean);
          setAppNames(appNames);

          if (appNames.length > 0 && !selectedAppName) {
            setSelectedAppName(appNames[0]);
          }
        } else {
          setErrorLocal('Failed to load app names.');
        }
        setIsAppNamesLoading(false);
      }
    } catch (err) {
      console.error("Error fetching app names:", err);
      setErrorLocal('Failed to load app names. Network error.');
      setIsAppNamesLoading(false);
    }
  };

  // Initial fetch of app names
  useEffect(() => {
    fetchAppNames();
  }, [API_BASE_URL]);

  const handleSendFCM = async () => {
    setIsLoading(true);
    setIsLoadingLocal(true);
    setSuccessLocal('');
    setErrorLocal('');

    // 1. Validation (Shared)
    if (!selectedAppName || !title || !message) {
      setErrorLocal('Please fill in App Name, Notification Title, and Message fields.');
      setIsLoading(false);
      setIsLoadingLocal(false);
      return;
    }

    if (notificationType === 'device' && !deviceToken) {
      setErrorLocal('Please provide a Device Token.');
      setIsLoading(false);
      setIsLoadingLocal(false);
      return;
    }

    if (notificationType === 'topic' && !topic) {
      setErrorLocal('Please provide a Topic.');
      setIsLoading(false);
      setIsLoadingLocal(false);
      return;
    }

    // 2. Build Payload (Shared)
    const payload = {
      body: message,
      title: title,
      appName: selectedAppName,
      type: fcmVersion === 'new' ? "1" : "2"
    };

    if (notificationType === 'device') {
      payload.device_token = deviceToken;
    } else if (notificationType === 'topic') {
      payload.topic = topic;
    }

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setErrorLocal('Socket connection failed.');
          setIsLoading(false);
          setIsLoadingLocal(false);
          return;
        }

        const subscriptionTopic = `/topic/sendFcm/${loginId}`;
        const subscription = client.subscribe(subscriptionTopic, (msg) => {
          try {
            const data = JSON.parse(msg.body);

            if (data.STATUS === 'SUCCESS') {
              setSuccessLocal('FCM notification sent successfully! 🎉');
              setSuccess('FCM notification sent!');

              // Reset Fields
              setTitle('');
              setMessage('');
              setDeviceToken('');
              setTopic('');

              // Refresh Stats (make sure this function supports socket too)
              fetchStats('push');
            } else {
              const errMsg = data.MESSAGE || 'Unknown error occurred.';
              setErrorLocal(`Failed to send FCM. ${errMsg}`);
              setError(`Failed to send FCM: ${errMsg}`);
            }
          } catch (e) {
            console.error('Failed to parse socket FCM response', e);
            setErrorLocal('Failed to parse server response.');
          } finally {
            subscription.unsubscribe();
            setIsLoading(false);
            setIsLoadingLocal(false);
          }
        });

        client.publish({
          destination: '/app/sendFcm',
          body: JSON.stringify(payload)
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/sendFcm`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.STATUS === 'SUCCESS') {
          setSuccessLocal('FCM notification sent successfully! 🎉');
          setSuccess('FCM notification sent!');
          setTitle('');
          setMessage('');
          setDeviceToken('');
          setTopic('');
          fetchStats('push');
        } else {
          setErrorLocal(`Failed to send FCM. ${data.MESSAGE || 'Unknown error occurred.'}`);
          setError(`Failed to send FCM: ${data.MESSAGE || 'Unknown error'}`);
        }
        setIsLoading(false);
        setIsLoadingLocal(false);
      }

    } catch (err) {
      console.error("FCM Send error:", err);
      setErrorLocal('Failed to send FCM. Network error or invalid input.');
      setError('Network error or invalid input for FCM.');
      setIsLoading(false);
      setIsLoadingLocal(false);
    }
  };

  const handleAppSelect = (appName) => {
    setSelectedAppName(appName);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleNotificationTypeChange = (type) => {
    setNotificationType(type);
    // Clear the other field when switching
    if (type === 'device') {
      setTopic('');
    } else {
      setDeviceToken('');
    }
  };

  const handleFcmVersionChange = (version) => {
    setFcmVersion(version);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-8 relative">
        {/* App Selection */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl relative z-30">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">Application Settings</h3>
          </div>

          <label className="block text-white/90 text-sm font-medium mb-3">
            Select App Name
          </label>

          {isAppNamesLoading ? (
            <div className="w-full px-4 py-4 bg-white/5 border border-white/20 rounded-xl text-white flex items-center">
              <Loader2 className="w-5 h-5 mr-3 animate-spin text-blue-400" />
              <span>Loading available applications...</span>
            </div>
          ) : appNames.length > 0 ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300 flex items-center justify-between font-medium hover:bg-white/15"
                disabled={isLoadingLocal || isUploading}
              >
                <span>{selectedAppName || 'Select an app...'}</span>
                <ChevronDown className={`w-5 h-5 text-white/60 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                    onClick={() => setIsDropdownOpen(false)}
                  />

                  {/* Dropdown Content */}
                  <div className="absolute top-full left-0 right-0 mt-2 bg-slate-900 backdrop-blur-xl border border-red/30 rounded-xl shadow-2xl z-[9999] overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-white/10">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search apps..."
                          className="w-full pl-10 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
                        />
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 hover:text-white/80"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* App List with Scroll */}
                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                      {filteredAppNames.length > 0 ? (
                        filteredAppNames.map((name) => (
                          <button
                            key={name}
                            onClick={() => handleAppSelect(name)}
                            className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors duration-200 ${selectedAppName === name
                              ? "bg-blue-500/20 text-blue-200"
                              : "text-white/80"
                              }`}
                          >
                            {name}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-white/50 text-sm">
                          No apps found matching "{searchTerm}"
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <input
              type="text"
              value={selectedAppName}
              onChange={(e) => setSelectedAppName(e.target.value)}
              placeholder="Enter App Name (no apps found)"
              className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
              disabled={isLoadingLocal || isUploading}
            />
          )}
        </div>

        {/* FCM Version Selection - NEW SECTION */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-2xl">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-orange-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">FCM Version</h3>
          </div>

          <div className="space-y-4">
            <label className="block text-white/90 text-sm font-medium">
              Select FCM Version
            </label>
            <div className="flex space-x-3">
              <button
                onClick={() => handleFcmVersionChange('new')}
                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${fcmVersion === 'new'
                  ? 'bg-green-500/30 border-2 border-green-400/50 text-green-200'
                  : 'bg-white/5 border-2 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                disabled={isLoadingLocal || isUploading}
              >
                🆕 New Version FCM
              </button>
              <button
                onClick={() => handleFcmVersionChange('old')}
                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${fcmVersion === 'old'
                  ? 'bg-amber-500/30 border-2 border-amber-400/50 text-amber-200'
                  : 'bg-white/5 border-2 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                disabled={isLoadingLocal || isUploading}
              >
                🔄 Old Version FCM
              </button>
            </div>

            {/* Version Info */}
            <div className="mt-3 p-3 bg-white/5 rounded-lg">
              <p className="text-white/70 text-sm">
                {fcmVersion === 'new' ? (
                  <span>✨ Using <strong className="text-green-300">New Version FCM</strong> - Latest features and improved delivery</span>
                ) : (
                  <span>🔄 Using <strong className="text-amber-300">Old Version FCM</strong> - Legacy compatibility mode</span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Send Notification Section */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 space-y-6 shadow-2xl">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Bell className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">Send Push Notification</h3>
          </div>

          {(successLocal || errorLocal) && (
            <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${successLocal
              ? 'bg-green-500/20 border-green-500/40 text-green-200'
              : 'bg-red-500/20 border-red-500/40 text-red-200'
              }`}>
              {successLocal ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
              <span className="font-medium">{successLocal || errorLocal}</span>
            </div>
          )}

          {/* Notification Type Selector */}
          <div className="space-y-4">
            <label className="block text-white/90 text-sm font-medium">
              Notification Target
            </label>
            <div className="flex space-x-3">
              <button
                onClick={() => handleNotificationTypeChange('device')}
                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${notificationType === 'device'
                  ? 'bg-blue-500/30 border-2 border-blue-400/50 text-blue-200'
                  : 'bg-white/5 border-2 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                disabled={isLoadingLocal || isUploading}
              >
                📱 Individual Device
              </button>
              <button
                onClick={() => handleNotificationTypeChange('topic')}
                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${notificationType === 'topic'
                  ? 'bg-cyan-500/30 border-2 border-cyan-400/50 text-cyan-200'
                  : 'bg-white/5 border-2 border-white/10 text-white/70 hover:bg-white/10'
                  }`}
                disabled={isLoadingLocal || isUploading}
              >
                📢 Topic (Multiple Devices)
              </button>
            </div>
          </div>

          {/* Target Input */}
          <div>
            {notificationType === 'device' ? (
              <div>
                <label className="block text-white/90 text-sm font-medium mb-3">
                  Device Token <span className="text-blue-300">(for individual device)</span>
                </label>
                <input
                  type="text"
                  value={deviceToken}
                  onChange={(e) => setDeviceToken(e.target.value)}
                  placeholder="Enter device token..."
                  className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                  disabled={isLoadingLocal || isUploading}
                />
              </div>
            ) : (
              <div>
                <label className="block text-white/90 text-sm font-medium mb-3">
                  Topic <span className="text-cyan-300">(for multiple devices)</span>
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Enter topic (e.g., news_updates)"
                  className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all duration-300"
                  disabled={isLoadingLocal || isUploading}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-white/90 text-sm font-medium mb-3">
              Notification Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter notification title..."
              className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300"
              disabled={isLoadingLocal || isUploading}
            />
          </div>

          <div>
            <label className="block text-white/90 text-sm font-medium mb-3">
              Message Content
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your push notification message..."
              rows={4}
              className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all duration-300 resize-none"
              disabled={isLoadingLocal || isUploading}
            />
          </div>

          <button
            onClick={handleSendFCM}
            disabled={
              isLoadingLocal ||
              isUploading ||
              !selectedAppName ||
              !title ||
              !message ||
              (notificationType === 'device' && !deviceToken) ||
              (notificationType === 'topic' && !topic)
            }
            className="w-full py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl disabled:cursor-not-allowed transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 hover:from-blue-600 hover:via-purple-600 hover:to-cyan-600 disabled:opacity-50 group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            {isLoadingLocal ? (
              <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Sending Notification...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-3 text-white relative z-10">
                <Send className="w-5 h-5" />
                <span>Send Push Notification</span>
                <Zap className="w-4 h-4" />
              </div>
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(100, 116, 139, 0.5) rgba(51, 65, 85, 0.5);
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: rgba(51, 65, 85, 0.5);
          border-radius: 0.5rem;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(100, 116, 139, 0.5);
          border-radius: 0.5rem;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(100, 116, 139, 0.7);
        }
      `}</style>
    </div>
  );
}